import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, message, Layout, Typography, Space, Tag, Row, Col, Alert } from 'antd';
import { PhoneOutlined, SafetyCertificateOutlined, ReloadOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const { Content } = Layout;
const { Title, Text } = Typography;

const Login = () => {
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [authMode, setAuthMode] = useState('otp'); // otp | password | forgot
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [counter, setCounter] = useState(0);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  // Fresh login page: clear stale client session; server logout is best-effort
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('multi_account');
    sessionStorage.removeItem('selection_data');
    api.post('/auth/logout').catch(() => {});
  }, []);

  // countdown for resend
  useEffect(() => {
    if (!counter) return;
    const t = setInterval(() => setCounter((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [counter]);

  const normalizePhone = (p) => String(p || '').replace(/[^0-9]/g, '').slice(-10);
  const getErrorMessage = (error, fallback) => {
    if (!error?.response) {
      const code = error?.code || '';
      if (code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
        return 'Cannot reach server. Start backend (npm run dev on port 4000).';
      }
      return 'Cannot reach server. Check that backend is running on port 4000.';
    }
    return error.response?.data?.message || fallback;
  };

  const completeLogin = (res, ph, code) => {
    if (res.data?.requireSignup) {
      message.info('Not registered. Please complete signup.');
      setTimeout(() => navigate(`/signup-admin?phone=${ph}`), 500);
      return true;
    }
    if (res.data?.requireSelection) {
      sessionStorage.clear();
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      if (res.data.token) localStorage.setItem('token', res.data.token);
      const selectionData = { organizations: res.data.organizations, phone: ph, code, canCreateOrg: res.data.canCreateOrg };
      sessionStorage.setItem('selection_data', JSON.stringify(selectionData));
      navigate('/home', { state: selectionData });
      return true;
    }
    if (res.data?.success) {
      sessionStorage.clear();
      localStorage.removeItem('multi_account');
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      message.success('Login successful');
      const role = res.data?.user?.role || 'admin';
      const isSuperadminPanel = !!res.data?.user?.isSuperadminPanel;

      let dest = '/dashboard';
      if (role === 'superadmin') dest = '/superadmin/clients';
      else if (isSuperadminPanel) dest = '/superadmin/dashboard';
      else if (role === 'channel_partner') dest = '/partner/clients';

      setTimeout(() => navigate(dest), 800);
      return true;
    }
    return false;
  };

  const sendOtp = async (purpose) => {
    if (purpose === 'reset') {
      const ph = normalizePhone(phone);
      if (!ph || ph.length !== 10) {
        message.error('Enter valid 10-digit phone number');
        return;
      }
      const em = String(email || '').trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!em || !emailRegex.test(em)) {
        message.error('Enter a valid registered email address');
        return;
      }
      try {
        setSending(true);
        const res = await api.post('/auth/send-otp', { phone: ph, email: em, purpose: 'reset' });
        if (res.data?.success) {
          message.success(res.data.message || 'OTP sent to your email');
          setStep('otp');
          setCounter(60);
          setOtp(['', '', '', '', '', '']);
        } else {
          message.error(res.data?.message || 'Failed to send OTP');
        }
      } catch (e) {
        message.error(getErrorMessage(e, 'Failed to send OTP'));
      } finally {
        setSending(false);
      }
      return;
    }

    const ph = normalizePhone(phone);
    if (!ph || ph.length !== 10) {
      message.error('Enter valid 10-digit phone number');
      return;
    }
    try {
      setSending(true);
      const res = await api.post('/auth/send-otp', { phone: ph, purpose });
      if (res.data?.success) {
        message.success('OTP sent');
        setStep('otp');
        setCounter(60);
        setOtp(['', '', '', '', '', '']);
      } else {
        message.error(res.data?.message || 'Failed to send OTP');
      }
    } catch (e) {
      message.error(getErrorMessage(e, 'Failed to send OTP'));
    } finally {
      setSending(false);
    }
  };

  const onPasteOtp = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    const digits = String(text || '').replace(/\D/g, '').slice(0, 6);
    if (digits.length) {
      const arr = new Array(6).fill('');
      for (let i = 0; i < Math.min(6, digits.length); i++) arr[i] = digits[i];
      setOtp(arr);
      e.preventDefault();
    }
  };

  const setOtpAt = (idx, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[idx] = d;
    setOtp(next);
    if (d && idx < 5) inputsRef.current[idx + 1]?.focus?.();
  };

  const loginWithPassword = async () => {
    setLoginError('');
    const ph = normalizePhone(phone);
    if (!ph || ph.length !== 10) {
      setLoginError('Enter valid 10-digit phone number');
      return;
    }
    if (!password) {
      setLoginError('Enter password');
      return;
    }
    try {
      setLoggingIn(true);
      const res = await api.post('/auth/login', { phone: ph, password });
      if (!completeLogin(res, ph)) {
        setLoginError(res.data?.message || 'Login failed. Please check your credentials.');
      }
    } catch (e) {
      setLoginError(getErrorMessage(e, 'Incorrect password. Please try again.'));
    } finally {
      setLoggingIn(false);
    }
  };

  const verify = async (codeOverride, orgIdOverride) => {
    const ph = normalizePhone(phone);
    const code = codeOverride || otp.join('');
    if (!ph || code.length !== 6) {
      message.error('Enter 6-digit OTP');
      return;
    }
    try {
      setVerifying(true);
      const res = await api.post('/auth/verify-otp', { phone: ph, code, orgId: orgIdOverride });
      if (!completeLogin(res, ph, code)) {
        message.error(res.data?.message || 'Login failed');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Login failed');
    } finally {
      setVerifying(false);
    }
  };

  const resetPassword = async () => {
    const ph = normalizePhone(phone);
    const em = String(email || '').trim().toLowerCase();
    const code = otp.join('');
    if (!ph || ph.length !== 10) {
      message.error('Enter valid 10-digit phone number');
      return;
    }
    if (!em) {
      message.error('Enter registered email');
      return;
    }
    if (code.length !== 6) {
      message.error('Enter 6-digit OTP');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      message.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error('Passwords do not match');
      return;
    }
    try {
      setResetting(true);
      const res = await api.post('/auth/reset-password', { phone: ph, email: em, code, newPassword });
      if (res.data?.success) {
        message.success(res.data.message || 'Password updated. Please log in.');
        setAuthMode('password');
        setStep('phone');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setEmail('');
        setOtp(['', '', '', '', '', '']);
      } else {
        message.error(res.data?.message || 'Failed to reset password');
      }
    } catch (e) {
      message.error(getErrorMessage(e, 'Failed to reset password'));
    } finally {
      setResetting(false);
    }
  };

  const switchMode = (mode) => {
    setLoginError('');
    setAuthMode(mode);
    setStep('phone');
    setOtp(['', '', '', '', '', '']);
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setEmail('');
    setCounter(0);
  };

  const primaryBtnStyle = {
    width: '100%',
    height: 52,
    fontWeight: 600,
    fontSize: 16,
    borderRadius: 12,
    background: '#2463EB',
    border: 'none',
    boxShadow: '0 4px 14px rgba(36,99,235,0.25)'
  };

  const inputStyle = { height: 52, borderRadius: 12, border: '1px solid #d0d5dd' };

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Content style={{ padding: 0 }}>
        <Row style={{ minHeight: '100vh' }}>
          {/* Left Column - Design Illustration */}
          <Col
            xs={0}
            md={10}
            lg={12}
            style={{
              background: 'linear-gradient(135deg, #f8fbff 0%, #e0eaff 100%)',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '60px',
              borderRight: '1px solid #f0f0f0'
            }}
          >
            {/* Background decorative elements with the theme colors */}
            <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(36,99,235,0.05)' }}></div>
            <div style={{ position: 'absolute', bottom: '10%', left: '-5%', width: '250px', height: '250px', borderRadius: '50%', background: 'rgba(18,101,205,0.05)' }}></div>

            <div style={{ position: 'relative', zIndex: 2 }}>
              <div style={{ marginBottom: 24 }}>
                <Tag color="blue" style={{ borderRadius: 20, padding: '2px 12px', fontWeight: 600, background: 'rgba(36,99,235,0.1)', color: '#2463EB', border: 'none' }}>
                  SYSTEM STATUS
                </Tag>
              </div>
              <Title level={1} style={{ color: '#101828', fontSize: '3.5rem', fontWeight: 800, lineHeight: 1.1, marginBottom: 24 }}>
                Advanced Intelligence for <span style={{ color: '#2463EB' }}>Modern Enterprise.</span>
              </Title>

              <div style={{ marginTop: 48, position: 'relative' }}>
                <div style={{
                  maxWidth: 640
                }}>
                  <img
                    src="/pina.png"
                    alt="Dashboard Preview"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          </Col>

          {/* Right Column - Form */}
          <Col xs={24} md={14} lg={12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
            <div style={{ width: '100%', maxWidth: 460, padding: '40px' }}>
              <div style={{ marginBottom: 48 }}>
                <Title level={2} style={{ fontWeight: 800, marginBottom: 12, color: '#2564EB', fontSize: 32 }}>
                  {authMode === 'forgot' ? 'Reset password' : 'Welcome back'}
                </Title>
                <Text type="secondary" style={{ fontSize: 16, color: '#667085' }}>
                  {authMode === 'forgot'
                    ? 'Verify your phone and associated email with OTP, then set a new password.'
                    : authMode === 'otp'
                      ? 'Enter your phone number to receive a login OTP.'
                      : 'Sign in with your phone and password.'}
                </Text>
              </div>

              {authMode === 'password' && (
                <div className="login-form-container">
                  {loginError && (
                    <Alert
                      type="error"
                      message={loginError}
                      showIcon
                      closable
                      onClose={() => setLoginError('')}
                      style={{ marginBottom: 20, borderRadius: 10, fontSize: 14 }}
                    />
                  )}
                  <Form layout="vertical" size="large" onFinish={loginWithPassword}>
                    <Form.Item required>
                      <Input
                        prefix={<PhoneOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="Enter 10-digit phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value); if (loginError) setLoginError(''); }}
                        maxLength={10}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <Form.Item required>
                      <Input.Password
                        prefix={<LockOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="Enter password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (loginError) setLoginError(''); }}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 16 }}>
                      <span
                        onClick={() => switchMode('forgot')}
                        style={{ color: '#2463EB', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                      >
                        Forgot password?
                      </span>
                    </div>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button type="primary" htmlType="submit" loading={loggingIn} style={primaryBtnStyle}>
                        Log In
                      </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                      <Text type="secondary" style={{ fontSize: 15 }}>
                        Prefer OTP?{' '}
                        <span
                          onClick={() => switchMode('otp')}
                          style={{ color: '#2463EB', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Login with OTP
                        </span>
                      </Text>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                      <Text type="secondary" style={{ fontSize: 15 }}>
                        Don't have an account?{' '}
                        <span
                          onClick={() => navigate('/signup-admin')}
                          style={{ color: '#2463EB', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Sign up
                        </span>
                      </Text>
                    </div>
                  </Form>
                </div>
              )}

              {authMode === 'otp' && step === 'phone' && (
                <div className="login-form-container">
                  <Form layout="vertical" size="large" onFinish={() => sendOtp('login')}>
                    <Form.Item
                      required
                    >
                      <Input
                        prefix={<PhoneOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="Enter 10-digit phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        maxLength={10}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={sending}
                        style={primaryBtnStyle}
                      >
                        Continue
                      </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                      <Text type="secondary" style={{ fontSize: 15 }}>
                        Use password instead?{' '}
                        <span
                          onClick={() => switchMode('password')}
                          style={{ color: '#2463EB', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Login with password
                        </span>
                      </Text>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                      <Text type="secondary" style={{ fontSize: 15 }}>
                        Don't have an account? {' '}
                        <span
                          onClick={() => navigate('/signup-admin')}
                          style={{ color: '#2463EB', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Sign up
                        </span>
                      </Text>
                    </div>
                  </Form>
                </div>
              )}

              {authMode === 'otp' && step === 'otp' && (
                <div className="otp-form-container">
                  <div style={{ marginBottom: 32 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 18, color: '#101828' }}>Confirm the OTP</Text>
                    <Text style={{ color: '#667085', fontSize: 15 }}>Enter the 6-digit OTP sent to <span style={{ color: '#2463EB', fontWeight: 700 }}>+91-{phone}</span></Text>
                  </div>

                  <Space size={8} onPaste={onPasteOtp} style={{ display: 'flex', width: '100%', justifyContent: 'space-between', marginBottom: 32 }}>
                    {otp.map((v, i) => (
                      <Input
                        key={i}
                        ref={(el) => (inputsRef.current[i] = el)}
                        value={v}
                        onChange={(e) => setOtpAt(i, e.target.value)}
                        maxLength={1}
                        style={{
                          width: 48,
                          height: 52,
                          textAlign: 'center',
                          borderRadius: 12,
                          fontSize: 20,
                          fontWeight: 700,
                          border: '2px solid #eaecf0',
                          background: '#f9fafb',
                          color: '#101828'
                        }}
                        inputMode="numeric"
                      />
                    ))}
                  </Space>

                  <Button
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    loading={verifying}
                    onClick={() => verify()}
                    style={{ ...primaryBtnStyle, marginBottom: 16 }}
                  >
                    Log In
                  </Button>

                  <div style={{ textAlign: 'center' }}>
                    <Button type="link" icon={<ReloadOutlined />} onClick={() => sendOtp('login')} disabled={counter > 0} style={{ color: '#667085', fontWeight: 500 }}>
                      Resend OTP {counter > 0 ? `(${counter}s)` : ''}
                    </Button>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={() => switchMode('password')} style={{ color: '#667085', fontWeight: 500 }}>
                      Back to password login
                    </Button>
                  </div>
                </div>
              )}

              {authMode === 'forgot' && step === 'phone' && (
                <div className="login-form-container">
                  <Form layout="vertical" size="large" onFinish={() => sendOtp('reset')}>
                    <Form.Item required style={{ marginBottom: 16 }}>
                      <Input
                        prefix={<PhoneOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="Enter 10-digit phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        maxLength={10}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <Form.Item required style={{ marginBottom: 0 }}>
                      <Input
                        prefix={<MailOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="Enter registered email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
                      <Button type="primary" htmlType="submit" loading={sending} style={primaryBtnStyle}>
                        Send OTP
                      </Button>
                    </Form.Item>
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                      <span
                        onClick={() => switchMode('password')}
                        style={{ color: '#2463EB', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Back to login
                      </span>
                    </div>
                  </Form>
                </div>
              )}

              {authMode === 'forgot' && step === 'otp' && (
                <div className="otp-form-container">
                  <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 18, color: '#101828' }}>Verify OTP and set password</Text>
                    <Text style={{ color: '#667085', fontSize: 15 }}>Enter the 6-digit OTP sent to <span style={{ color: '#2463EB', fontWeight: 700 }}>{email}</span></Text>
                  </div>

                  <Space size={8} onPaste={onPasteOtp} style={{ display: 'flex', width: '100%', justifyContent: 'space-between', marginBottom: 24 }}>
                    {otp.map((v, i) => (
                      <Input
                        key={i}
                        ref={(el) => (inputsRef.current[i] = el)}
                        value={v}
                        onChange={(e) => setOtpAt(i, e.target.value)}
                        maxLength={1}
                        style={{
                          width: 48,
                          height: 52,
                          textAlign: 'center',
                          borderRadius: 12,
                          fontSize: 20,
                          fontWeight: 700,
                          border: '2px solid #eaecf0',
                          background: '#f9fafb',
                          color: '#101828'
                        }}
                        inputMode="numeric"
                      />
                    ))}
                  </Space>

                  <Form layout="vertical" size="large" onFinish={resetPassword}>
                    <Form.Item required>
                      <Input.Password
                        prefix={<LockOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="New password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <Form.Item required>
                      <Input.Password
                        prefix={<LockOutlined style={{ color: '#98a2b3' }} />}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={resetting} style={primaryBtnStyle}>
                      Update password
                    </Button>
                  </Form>

                  <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <Button type="link" icon={<ReloadOutlined />} onClick={() => sendOtp('reset')} disabled={counter > 0} style={{ color: '#667085', fontWeight: 500 }}>
                      Resend OTP {counter > 0 ? `(${counter}s)` : ''}
                    </Button>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={() => switchMode('password')} style={{ color: '#667085', fontWeight: 500 }}>
                      Back to login
                    </Button>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 64, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 14, color: '#667085' }}>
                  By continuing you agree to our <a href="https://vetansutra.com/policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2463EB', fontWeight: 600 }}>Privacy Policy</a>
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default Login;
